// src/components/GT7RaceConfigPanel.js
// GT7 race configuration — the SAME sections as event creation (founder
// 2026-07-14: the League Command Center draws in the create-event format):
//   1. Race Configuration  — laps (or Endurance duration), pit stops, tire
//      change, live duration estimate
//   2. Vehicle Category    — class pills + description
//   3. Track Selection     — one searchable dropdown per race, or rotate
//   4. Advanced Settings   — GT7_LOBBY_SETTINGS overrides (locked/requires/
//      multi-select semantics identical to CreateEventsScreen)
//
// Controlled component: the parent owns carCategory, raceTracks (array of
// track names aligned to race number; null = rotate every race; null SLOTS
// rotate that race), and gt7Config { lapsMode, lapsOverride,
// raceDistanceMiles, perRace, pitStopsOverride, tireChangeOverride,
// enduranceMinutes, lobbySettingsOverrides }.
//
// Race length comes in three modes (founder 2026-07-14):
//   'same'     — every race runs lapsOverride laps (default)
//   'distance' — every race covers raceDistanceMiles (8–200 mi); laps are
//                derived per track at round creation: ceil(miles / length)
//   'perRace'  — perRace[i] = { unit: 'laps'|'miles', value } per race
// The server mirrors the derivation in leagues.js leagueRaceLaps().
import React, { useState } from 'react';
import {
  View, Text, TouchableOpacity, TextInput, ScrollView, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { GT7_LOBBY_SETTINGS, GT7_SETTING_SECTION_LABELS } from '../constants/gameData';

const GOLD = '#FCD000';

// Duration estimation — mirrors CreateEventsScreen.
const VEHICLE_AVG_MPH = { gr1: 115, gr2: 108, gr3: 100, gr4: 88, grB: 75, no_limit: 100 };
const TRACK_TYPE_FACTOR = { road: 1.0, oval: 1.4, rally: 0.55 };
const trackLengthFactor = (mi) => (mi < 2 ? 0.7 : mi > 6 ? 1.2 : 1.0);
const PIT_STOP_MINUTES = 0.5;
const formatEnduranceTime = (mins) => {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};
const estimateRaceMinutes = (track, laps, carCategory, pitStops) => {
  if (!track || !laps || !track.lengthMiles) return 0;
  const baseAvg = VEHICLE_AVG_MPH[carCategory] || VEHICLE_AVG_MPH.no_limit;
  const typeFactor = TRACK_TYPE_FACTOR[track.type || 'road'] || 1.0;
  const lenFactor = trackLengthFactor(track.lengthMiles);
  const avgMph = baseAvg * typeFactor * lenFactor;
  const minPerLap = (track.lengthMiles / avgMph) * 60;
  return minPerLap * laps + (pitStops || 0) * PIT_STOP_MINUTES;
};

function Pill({ label, active, onPress, disabled }) {
  return (
    <TouchableOpacity onPress={onPress} disabled={disabled}
      style={[st.pill, active && st.pillActive, disabled && st.pillDisabled]}>
      <Text style={[st.pillText, active && st.pillTextActive]}>{label}</Text>
    </TouchableOpacity>
  );
}

function Stepper({ value, onChange, min, max, step = 1, suffix = '', disabled }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <TouchableOpacity disabled={disabled} onPress={() => onChange(Math.max(min, value - step))}
        style={[st.stepBtn, disabled && st.stepBtnDisabled]}>
        <Text style={st.stepBtnText}>-</Text>
      </TouchableOpacity>
      <Text style={st.stepValue}>{value}{suffix}</Text>
      <TouchableOpacity disabled={disabled} onPress={() => onChange(Math.min(max, value + step))}
        style={[st.stepBtn, disabled && st.stepBtnDisabled]}>
        <Text style={st.stepBtnText}>+</Text>
      </TouchableOpacity>
    </View>
  );
}

export default function GT7RaceConfigPanel({
  gp, editable, racesCount,
  carCategory, onCarCategory,
  raceTracks, onRaceTracks,       // array|null; null = rotate all
  gt7Config, onGt7Config,
}) {
  const cfg = gt7Config || {};
  const overrides = cfg.lobbySettingsOverrides || {};
  const lapsMode = cfg.lapsMode || 'same';
  const laps = cfg.lapsOverride ?? 5;
  const raceDistanceMiles = cfg.raceDistanceMiles ?? 8;
  const perRace = Array.isArray(cfg.perRace) ? cfg.perRace : [];
  const pitStops = cfg.pitStopsOverride ?? 0;
  const tireChange = cfg.tireChangeOverride === true;
  const enduranceMinutes = cfg.enduranceMinutes ?? 60;
  const setCfg = (patch) => onGt7Config({ ...cfg, ...patch });

  // Per-race length entries, padded to racesCount with the flat-laps default.
  const entryFor = (i) => perRace[i] || { unit: 'laps', value: laps };
  const setEntry = (i, patch) => {
    const next = Array.from({ length: racesCount }, (_, k) => ({ ...entryFor(k) }));
    next[i] = { ...next[i], ...patch };
    setCfg({ perRace: next });
  };
  // Miles → laps on a given track (matches the server: covers at least the
  // distance, capped at GT7's 200-lap ceiling). null when track unknown.
  const lapsFromMiles = (miles, track) => {
    const len = Number(track?.lengthMiles);
    const mi = Number(miles);
    if (!Number.isFinite(len) || len <= 0 || !Number.isFinite(mi) || mi <= 0) return null;
    return Math.min(200, Math.max(1, Math.ceil(mi / len)));
  };
  const setOverride = (sectionKey, settingKey, value) => setCfg({
    lobbySettingsOverrides: {
      ...overrides,
      [sectionKey]: { ...(overrides[sectionKey] || {}), [settingKey]: value },
    },
  });

  const isEndurance = String(overrides?.details?.roomMode
    || GT7_LOBBY_SETTINGS?.details?.roomMode?.value || '')
    .toLowerCase().includes('endurance');

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [sectionOpen, setSectionOpen] = useState({});
  const [trackDropdownOpen, setTrackDropdownOpen] = useState(-1);
  const [trackSearch, setTrackSearch] = useState('');
  const [settingDropdownOpen, setSettingDropdownOpen] = useState(null);

  // Racing games only: a battle-royale race league (Warzone) reuses the race
  // machinery but has no laps/distance/lobby dials - the always-shown
  // sections below must not render for it. (The parent gates too; this is
  // the component's own guard.)
  const isRacingType = gp?.type === 'racing';

  const customTracks = Array.isArray(raceTracks);
  const trackFor = (i) => (customTracks ? raceTracks[i] || null : null);
  const setTrackFor = (i, name) => {
    const next = customTracks ? [...raceTracks] : Array.from({ length: racesCount }, () => null);
    while (next.length < racesCount) next.push(null);
    next[i] = name;
    onRaceTracks(next);
    setTrackDropdownOpen(-1);
    setTrackSearch('');
  };

  const estimateTrack = (customTracks && raceTracks.find(Boolean)
    ? gp.tracks?.find((t) => t.name === raceTracks.find(Boolean))
    : gp.tracks?.[0]) || null;

  // The track race i will actually run: its picked track, else the rotation
  // slot the server would draw (trackList[(round-1) % length]). Lets
  // distance/per-race-miles modes preview the derived lap count.
  const resolvedTrackObjFor = (i) => {
    const picked = trackFor(i);
    if (picked) return gp.tracks?.find((t) => t.name === picked) || null;
    const list = gp.tracks || [];
    return list.length ? list[i % list.length] : null;
  };

  // Laps the estimate box should assume (race 1 under the active mode).
  const estimateLaps = (() => {
    if (lapsMode === 'distance') {
      return lapsFromMiles(raceDistanceMiles, resolvedTrackObjFor(0) || estimateTrack) ?? laps;
    }
    if (lapsMode === 'perRace') {
      const e = entryFor(0);
      if (e.unit === 'miles') {
        return lapsFromMiles(e.value, resolvedTrackObjFor(0) || estimateTrack) ?? laps;
      }
      const n = Number(e.value);
      return Number.isFinite(n) && n > 0 ? Math.min(200, Math.floor(n)) : laps;
    }
    return laps;
  })();

  return (
    <View>
      {/* ── 1. Race Configuration ─────────────────────────────────────────── */}
      {isRacingType && (
      <View style={st.panel}>
        <Text style={st.h2}>Race Configuration</Text>
        {isEndurance ? (
          <>
            <View style={st.rowBetween}>
              <Text style={st.note}>Race Duration</Text>
              <Text style={[st.note, st.goldStrong]}>{formatEnduranceTime(enduranceMinutes)}</Text>
            </View>
            {Platform.OS === 'web' ? (
              <input type="range" min="1" max="1440" value={enduranceMinutes}
                disabled={!editable}
                onChange={(e) => setCfg({ enduranceMinutes: Number(e.target.value) })}
                style={{ width: '100%', accentColor: GOLD, marginTop: 4 }} />
            ) : (
              <Stepper value={enduranceMinutes} min={1} max={1440} step={5} disabled={!editable}
                onChange={(v) => setCfg({ enduranceMinutes: v })} />
            )}
            <Text style={st.tinyNote}>Endurance races end after the configured duration. GT7 supports up to 24h.</Text>
          </>
        ) : (
          <>
            <Text style={[st.note, { fontWeight: '700', marginTop: 8 }]}>Race Length</Text>
            <View style={st.pillRow}>
              <Pill label="Same laps" active={lapsMode === 'same'} disabled={!editable}
                onPress={() => setCfg({ lapsMode: 'same' })} />
              <Pill label="Distance" active={lapsMode === 'distance'} disabled={!editable}
                onPress={() => setCfg({ lapsMode: 'distance' })} />
              <Pill label="Per race" active={lapsMode === 'perRace'} disabled={!editable}
                onPress={() => setCfg({ lapsMode: 'perRace' })} />
            </View>

            {lapsMode === 'same' && (
              <>
                <View style={[st.rowBetween, { marginTop: 8 }]}>
                  <Text style={st.note}>Laps</Text>
                  <Text style={[st.note, st.goldStrong]}>{laps}</Text>
                </View>
                {Platform.OS === 'web' ? (
                  <input type="range" min="1" max="200" value={laps}
                    disabled={!editable}
                    onChange={(e) => setCfg({ lapsOverride: Number(e.target.value) })}
                    style={{ width: '100%', accentColor: GOLD, marginTop: 4 }} />
                ) : (
                  <Stepper value={laps} min={1} max={200} disabled={!editable}
                    onChange={(v) => setCfg({ lapsOverride: v })} />
                )}
                <Text style={st.tinyNote}>Every race runs {laps} laps, whatever the track.</Text>
              </>
            )}

            {lapsMode === 'distance' && (
              <>
                <View style={[st.rowBetween, { marginTop: 8 }]}>
                  <Text style={st.note}>Race Distance</Text>
                  <Text style={[st.note, st.goldStrong]}>{raceDistanceMiles} mi</Text>
                </View>
                {Platform.OS === 'web' ? (
                  <input type="range" min="8" max="200" value={raceDistanceMiles}
                    disabled={!editable}
                    onChange={(e) => setCfg({ raceDistanceMiles: Number(e.target.value) })}
                    style={{ width: '100%', accentColor: GOLD, marginTop: 4 }} />
                ) : (
                  <Stepper value={raceDistanceMiles} min={8} max={200} disabled={!editable}
                    onChange={(v) => setCfg({ raceDistanceMiles: v })} />
                )}
                <Text style={st.tinyNote}>
                  Every race covers {raceDistanceMiles} miles — each track runs however many
                  laps that takes (rounded up), so short circuits lap more and long ones less.
                </Text>
              </>
            )}

            {lapsMode === 'perRace' && (
              <>
                {Array.from({ length: racesCount }, (_, i) => {
                  const e = entryFor(i);
                  const isMiles = e.unit === 'miles';
                  const trackObj = resolvedTrackObjFor(i);
                  const derived = isMiles ? lapsFromMiles(e.value, trackObj) : null;
                  const clampEntry = () => {
                    const n = Number(e.value);
                    const lo = isMiles ? 8 : 1;
                    const dflt = isMiles ? 8 : laps;
                    const v = Number.isFinite(n) && n > 0 ? Math.min(200, Math.max(lo, Math.floor(n))) : dflt;
                    if (v !== e.value) setEntry(i, { value: v });
                  };
                  return (
                    <View key={i} style={[st.rowBetween, { marginTop: i === 0 ? 8 : 6 }]}>
                      <View style={{ flex: 1, paddingRight: 8 }}>
                        <Text style={st.note} numberOfLines={1}>
                          Race {i + 1}{trackObj ? ` · ${trackObj.name}` : ''}
                        </Text>
                        {derived != null && (
                          <Text style={st.tinyNote}>≈ {derived} laps ({trackObj.lengthMiles} mi/lap)</Text>
                        )}
                      </View>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <Pill label="Laps" active={!isMiles} disabled={!editable}
                          onPress={() => setEntry(i, { unit: 'laps', value: laps })} />
                        <Pill label="Miles" active={isMiles} disabled={!editable}
                          onPress={() => setEntry(i, { unit: 'miles', value: 8 })} />
                        <TextInput
                          style={st.perRaceInput}
                          editable={editable}
                          keyboardType="numeric"
                          value={e.value == null ? '' : String(e.value)}
                          onChangeText={(txt) => {
                            const digits = txt.replace(/[^0-9]/g, '');
                            setEntry(i, { value: digits === '' ? null : Number(digits) });
                          }}
                          onBlur={clampEntry}
                        />
                      </View>
                    </View>
                  );
                })}
                <Text style={st.tinyNote}>
                  Set each race in laps or miles (8–200 mi; a miles race runs enough laps to
                  cover the distance on its track).
                </Text>
              </>
            )}
          </>
        )}

        <View style={[st.rowBetween, { marginTop: 12 }]}>
          <Text style={[st.note, { flex: 1 }]}>Mandatory Pit Stops</Text>
          <Stepper value={pitStops} min={0} max={5} disabled={!editable}
            onChange={(v) => setCfg({ pitStopsOverride: v })} />
        </View>

        <View style={[st.rowBetween, { marginTop: 12 }]}>
          <Text style={[st.note, { flex: 1 }]}>Required Tire Type Change</Text>
          <Pill label={tireChange ? 'Required' : 'Off'} active={tireChange} disabled={!editable}
            onPress={() => setCfg({ tireChangeOverride: !tireChange })} />
        </View>

        {!isEndurance && (() => {
          const estTrack = resolvedTrackObjFor(0) || estimateTrack;
          if (!estTrack) return null;
          const minutes = estimateRaceMinutes(estTrack, estimateLaps, carCategory, pitStops);
          const display = minutes >= 60
            ? `~${Math.floor(minutes / 60)}h ${Math.round(minutes % 60)}m`
            : `~${Math.round(minutes)} min`;
          return (
            <View style={st.estimateBox}>
              <Text style={[st.note, st.goldStrong]}>
                Estimated {lapsMode === 'same' ? 'Race' : 'Race 1'} Duration: {display}
              </Text>
              <Text style={st.tinyNote}>
                Based on {estTrack.name} ({estTrack.lengthMiles} mi), {estimateLaps} laps
                {lapsMode === 'distance' ? ` to cover ${raceDistanceMiles} mi` : ''},
                {' '}{(gp.roomSettings?.common?.carCategory?.options || []).find((o) => o.key === carCategory)?.label || carCategory}. Rough estimate.
              </Text>
            </View>
          );
        })()}
      </View>
      )}

      {/* ── 2. Vehicle Category ───────────────────────────────────────────── */}
      {!!gp?.roomSettings?.common?.carCategory && (
        <View style={st.panel}>
          <Text style={st.h2}>{gp.roomSettings.common.carCategory.label || 'Vehicle Category'}</Text>
          <View style={st.pillRow}>
            {gp.roomSettings.common.carCategory.options.map((opt) => (
              <Pill key={opt.key} label={opt.label} active={carCategory === opt.key}
                disabled={!editable} onPress={() => onCarCategory(opt.key)} />
            ))}
          </View>
          {!!gp.roomSettings.common.carCategory.description && (
            <Text style={st.note}>{gp.roomSettings.common.carCategory.description}</Text>
          )}
        </View>
      )}

      {/* ── 3. Track Selection (1 per race) ───────────────────────────────── */}
      {Array.isArray(gp?.tracks) && gp.tracks.length > 0 && (
        <View style={st.panel}>
          <Text style={st.h2}>Track Selection (1 per Race)</Text>
          <View style={st.pillRow}>
            <Pill label="Rotate all tracks" active={!customTracks} disabled={!editable}
              onPress={() => onRaceTracks(null)} />
            <Pill label="Pick per race" active={customTracks} disabled={!editable}
              onPress={() => onRaceTracks(Array.from({ length: racesCount }, () => null))} />
          </View>
          {!customTracks && (
            <Text style={st.note}>A different circuit every race, drawn from the full GT7 track list — championship variety with zero setup.</Text>
          )}
          {customTracks && Array.from({ length: racesCount }, (_, i) => (
            <View key={i} style={{ marginTop: i > 0 ? 8 : 10 }}>
              <Text style={[st.note, { marginBottom: 4, fontWeight: '700' }]}>
                Race {i + 1}{i === racesCount - 1 ? ' (Final)' : ''}
              </Text>
              <TouchableOpacity
                style={[st.input, st.rowBetween]}
                disabled={!editable}
                onPress={() => { setTrackDropdownOpen((p) => (p === i ? -1 : i)); setTrackSearch(''); }}
              >
                <Text style={{ color: trackFor(i) ? '#fff' : '#666', flex: 1 }} numberOfLines={1}>
                  {trackFor(i) || 'Rotate (auto-pick)…'}
                </Text>
                <Text style={{ color: '#888', fontSize: 12 }}>
                  {trackFor(i) ? `${gp.tracks.find((t) => t.name === trackFor(i))?.lengthMiles ?? ''} mi` : ''}
                </Text>
                <Ionicons name={trackDropdownOpen === i ? 'chevron-up' : 'chevron-down'} size={16} color="#888" style={{ marginLeft: 8 }} />
              </TouchableOpacity>
              {trackDropdownOpen === i && (
                <View style={st.dropdown}>
                  <TextInput
                    style={[st.input, { margin: 8, marginBottom: 4 }]}
                    placeholder="Search tracks..."
                    placeholderTextColor="#666"
                    value={trackSearch}
                    onChangeText={setTrackSearch}
                  />
                  <ScrollView style={{ maxHeight: 280 }} nestedScrollEnabled>
                    {(() => {
                      const query = trackSearch.toLowerCase().trim();
                      const renderOption = (t) => (
                        <TouchableOpacity key={t.name} style={[st.dropdownRow, trackFor(i) === t.name && st.dropdownRowOn]}
                          onPress={() => setTrackFor(i, t.name)}>
                          <Text style={{ color: trackFor(i) === t.name ? '#ffd166' : '#c9d1d9', fontSize: 13 }}>{t.name}</Text>
                          <Text style={{ color: '#666', fontSize: 11 }}>{t.lengthMiles} mi</Text>
                        </TouchableOpacity>
                      );
                      const rotateOption = (
                        <TouchableOpacity key="__rotate" style={[st.dropdownRow, !trackFor(i) && st.dropdownRowOn]}
                          onPress={() => setTrackFor(i, null)}>
                          <Text style={{ color: !trackFor(i) ? '#ffd166' : '#c9d1d9', fontSize: 13, fontWeight: '800' }}>
                            🔄 Rotate (auto-pick)
                          </Text>
                        </TouchableOpacity>
                      );
                      if (query) {
                        return [rotateOption, ...gp.tracks
                          .filter((t) => t.name.toLowerCase().includes(query))
                          .map(renderOption)];
                      }
                      const CATEGORIES = [
                        { label: 'Road Course', type: 'road' },
                        { label: 'Ovals', type: 'oval' },
                        { label: 'Rally Tracks', type: 'rally' },
                      ];
                      const BUCKETS = [
                        { label: 'Short (under 2 mi)', test: (mi) => mi < 2 },
                        { label: 'Medium (2 to 4 mi)', test: (mi) => mi >= 2 && mi < 4 },
                        { label: 'Long (4+ mi)', test: (mi) => mi >= 4 },
                      ];
                      return [rotateOption, ...CATEGORIES.map(({ label: catLabel, type }) => {
                        const inCat = gp.tracks.filter((t) => (t.type || 'road') === type);
                        if (inCat.length === 0) return null;
                        return (
                          <View key={catLabel}>
                            <Text style={st.dropdownCat}>{catLabel}</Text>
                            {BUCKETS.map(({ label: bucketLabel, test }) => {
                              const inBucket = inCat.filter((t) => test(t.lengthMiles));
                              if (inBucket.length === 0) return null;
                              return (
                                <View key={bucketLabel}>
                                  <Text style={st.dropdownBucket}>{bucketLabel}</Text>
                                  {inBucket.map(renderOption)}
                                </View>
                              );
                            })}
                          </View>
                        );
                      })];
                    })()}
                  </ScrollView>
                </View>
              )}
            </View>
          ))}
        </View>
      )}

      {/* ── 4. Advanced Settings ──────────────────────────────────────────── */}
      {isRacingType && (
      <View style={st.panel}>
        <TouchableOpacity onPress={() => setAdvancedOpen((v) => !v)} style={st.rowBetween}>
          <Text style={st.h2}>Advanced Settings (Optional)</Text>
          <Ionicons name={advancedOpen ? 'chevron-up' : 'chevron-down'} size={18} color={GOLD} />
        </TouchableOpacity>
        {!advancedOpen && (
          <Text style={st.note}>
            Defaults match CritCash's competitive preset. Tap to customize race
            conditions, regulations, and assists for every race in this league.
          </Text>
        )}
        {advancedOpen && (
          <View style={{ marginTop: 8 }}>
            {Object.entries(GT7_LOBBY_SETTINGS).map(([sectionKey, sectionData]) => {
              const open = !!sectionOpen[sectionKey];
              const sectionLabel = GT7_SETTING_SECTION_LABELS?.[sectionKey] || sectionKey;
              return (
                <View key={sectionKey} style={{ marginTop: 8, borderTopWidth: 1, borderTopColor: '#1a1e35' }}>
                  <TouchableOpacity
                    onPress={() => setSectionOpen((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }))}
                    style={[st.rowBetween, { paddingVertical: 10 }]}
                  >
                    <Text style={st.sectionHead}>{sectionLabel}</Text>
                    <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={14} color="#9aa0b4" />
                  </TouchableOpacity>
                  {open && Object.entries(sectionData).map(([settingKey, settingDef]) => {
                    const overrideVal = overrides?.[sectionKey]?.[settingKey];
                    const currentValue = overrideVal !== undefined ? overrideVal : settingDef.value;
                    let requiresUnmet = false;
                    if (settingDef.requires) {
                      for (const [depKey, depVal] of Object.entries(settingDef.requires)) {
                        const depOverride = overrides?.[sectionKey]?.[depKey];
                        const depCurrent = depOverride !== undefined ? depOverride : sectionData?.[depKey]?.value;
                        if (depCurrent !== depVal) { requiresUnmet = true; break; }
                      }
                    }
                    const isDisabled = !editable || !!settingDef.locked || requiresUnmet;
                    const useDropdown = (settingDef.options || []).length >= 5;
                    const dropdownKey = `${sectionKey}.${settingKey}`;
                    const isDropdownOpen = settingDropdownOpen === dropdownKey;
                    return (
                      <View key={settingKey} style={{ paddingVertical: 8, paddingLeft: 4 }}>
                        <Text style={st.settingLabel}>
                          {settingDef.label}
                          {settingDef.locked && <Text style={st.settingFlag}> · locked</Text>}
                          {requiresUnmet && !settingDef.locked && <Text style={st.settingFlag}> · n/a</Text>}
                        </Text>
                        {!!settingDef.description && (
                          <Text style={st.settingDesc}>{settingDef.description}</Text>
                        )}
                        {settingDef.multiSelect ? (
                          <View style={st.pillRow}>
                            {(() => {
                              const selected = new Set(String(currentValue || '')
                                .split(',').map((s) => s.trim()).filter(Boolean));
                              return (settingDef.options || []).map((opt) => {
                                const isActive = selected.has(opt);
                                return (
                                  <Pill key={opt} label={opt} active={isActive} disabled={isDisabled}
                                    onPress={() => {
                                      let next;
                                      if (opt === 'All') {
                                        next = 'All';
                                      } else if (isActive) {
                                        const reduced = new Set(selected);
                                        reduced.delete(opt); reduced.delete('All');
                                        next = reduced.size === 0
                                          ? 'All'
                                          : (settingDef.options || []).filter((o) => reduced.has(o)).join(', ');
                                      } else {
                                        const added = new Set(selected);
                                        added.delete('All'); added.add(opt);
                                        next = (settingDef.options || []).filter((o) => added.has(o)).join(', ');
                                      }
                                      setOverride(sectionKey, settingKey, next);
                                    }} />
                                );
                              });
                            })()}
                          </View>
                        ) : useDropdown ? (
                          <View>
                            <TouchableOpacity
                              style={[st.input, st.rowBetween]}
                              disabled={isDisabled}
                              onPress={() => setSettingDropdownOpen(isDropdownOpen ? null : dropdownKey)}
                            >
                              <Text style={{ color: '#fff', flex: 1 }} numberOfLines={1}>{String(currentValue)}</Text>
                              <Ionicons name={isDropdownOpen ? 'chevron-up' : 'chevron-down'} size={14} color="#888" />
                            </TouchableOpacity>
                            {isDropdownOpen && (
                              <View style={st.dropdown}>
                                <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled>
                                  {(settingDef.options || []).map((opt) => (
                                    <TouchableOpacity key={opt} style={[st.dropdownRow, currentValue === opt && st.dropdownRowOn]}
                                      onPress={() => { setOverride(sectionKey, settingKey, opt); setSettingDropdownOpen(null); }}>
                                      <Text style={{ color: currentValue === opt ? '#ffd166' : '#c9d1d9', fontSize: 13 }}>{opt}</Text>
                                    </TouchableOpacity>
                                  ))}
                                </ScrollView>
                              </View>
                            )}
                          </View>
                        ) : (
                          <View style={st.pillRow}>
                            {(settingDef.options || []).map((opt) => (
                              <Pill key={opt} label={opt} active={currentValue === opt} disabled={isDisabled}
                                onPress={() => setOverride(sectionKey, settingKey, opt)} />
                            ))}
                          </View>
                        )}
                      </View>
                    );
                  })}
                </View>
              );
            })}
          </View>
        )}
      </View>
      )}
    </View>
  );
}

const st = {
  panel: {
    backgroundColor: '#0f0f0f', borderRadius: 12, borderWidth: 1,
    borderColor: '#1a1a1a', padding: 12, marginTop: 14,
  },
  h2: { color: '#fff', fontSize: 14, fontWeight: '900' },
  note: { color: '#8a8a8a', fontSize: 12, marginTop: 6, lineHeight: 16 },
  tinyNote: { color: '#666', fontSize: 10, marginTop: 4 },
  goldStrong: { color: '#ffd166', fontWeight: '700' },
  rowBetween: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  pillRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  pill: {
    borderWidth: 1, borderColor: '#2a2a2a', backgroundColor: '#151515',
    paddingVertical: 6, paddingHorizontal: 10, borderRadius: 10,
  },
  pillActive: { backgroundColor: GOLD, borderColor: GOLD },
  pillDisabled: { opacity: 0.4 },
  pillText: { color: '#ddd', fontWeight: '800', fontSize: 12 },
  pillTextActive: { color: '#000' },
  input: {
    backgroundColor: '#0f1115', borderColor: '#2a2f36', borderWidth: 1,
    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10,
    color: '#fff', fontSize: 14, marginTop: 2,
  },
  estimateBox: {
    marginTop: 12, padding: 10, backgroundColor: '#0d0f12',
    borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#ffd166',
  },
  dropdown: {
    backgroundColor: '#1b1e24', borderRadius: 8, borderWidth: 1,
    borderColor: '#2a2f36', maxHeight: 340, marginTop: 4,
  },
  dropdownRow: { padding: 10, borderBottomWidth: 1, borderBottomColor: '#1e2025' },
  dropdownRowOn: { backgroundColor: '#2a2f36' },
  dropdownCat: {
    color: '#ffd166', fontSize: 12, fontWeight: '700', paddingHorizontal: 10,
    paddingTop: 12, paddingBottom: 4, backgroundColor: '#0d0f12',
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  dropdownBucket: { color: '#888', fontSize: 10, paddingHorizontal: 10, paddingTop: 6, paddingBottom: 2 },
  sectionHead: {
    color: GOLD, fontSize: 13, fontWeight: '700',
    letterSpacing: 0.5, textTransform: 'uppercase',
  },
  perRaceInput: {
    backgroundColor: '#0f1115', borderColor: '#2a2f36', borderWidth: 1,
    borderRadius: 8, paddingHorizontal: 8, paddingVertical: 5,
    color: '#fff', fontSize: 13, width: 56, textAlign: 'center',
  },
  stepBtn: {
    paddingHorizontal: 12, paddingVertical: 4,
    backgroundColor: '#1e2025', borderRadius: 6,
  },
  stepBtnDisabled: { opacity: 0.4 },
  stepBtnText: { color: '#ffd166', fontSize: 16, fontWeight: '700' },
  stepValue: { color: '#fff', minWidth: 36, textAlign: 'center', fontSize: 14 },
  settingLabel: { color: '#c9d1d9', fontSize: 12, fontWeight: '600', marginBottom: 4 },
  settingFlag: { color: '#9aa0b4', fontSize: 10, fontWeight: '500' },
  settingDesc: { color: '#666', fontSize: 11, marginBottom: 4 },
};
